import { Component, Input, OnInit } from '@angular/core';
import { LoginGuardService } from 'src/app/api/login-guard.service';
import { PageModalController, PageModalService } from '../page-modal.service';
import { AuthService } from 'src/app/api/auth.service';
import { LangService } from 'src/app/core/lang.service';
import { WhitelabelService } from 'src/app/domain/whitelabel.service';
import { RoutesService } from 'src/app/api/routes.service';
export enum ALModal {
  AL_CONFIG_MODAL = 'AL_CONFIG_MODAL'
};

export interface IALModalConfig {
  currentLevel: ALConfigLevel,
  boardInfo: any,
  levelData: {
    level: ALConfigLevel,
    whitelist: string[]
  }[]
  query: {
    board_id?: number
  }
}

export enum ALConfigLevel {
  SYSTEM = 'SYSTEM',
  BOARD = 'BOARD'
}

export enum ALConfigTypes {
  EXTENSION = 'EXTENSION'
}

const MAX_CHARACTER_LIMIT = 999; 

interface IALConfig {
  order: number,
  header: string,
  description: string,
  whitelist: string[],
  blacklist: string[],         // Unused but in case for future
  domain_whitelist: string[],  // Unused but in case for future
  shown?: boolean
}

export const LevelConfig: { [key in ALConfigLevel]: IALConfig } = {
  [ALConfigLevel.SYSTEM]: {
    order: 0,
    header: 'lbl_ldb_config_lvl_sys',
    description: 'lbl_ldb_config_default_des',
    whitelist: [],
    blacklist: [],
    shown: true,
    domain_whitelist: []
  },
  [ALConfigLevel.BOARD]: {
    order: 1,
    header: 'lbl_ldb_config_lvl_brd',
    description: ALConfigLevel.BOARD,
    whitelist: [],
    blacklist: [],
    domain_whitelist: []
  }
}

@Component({
  selector: 'modal-assessmentlock-config',
  templateUrl: './modal-assessmentlock-config.component.html',
  styleUrls: ['./modal-assessmentlock-config.component.scss']
})
export class ModalAssessmentLockConfigComponent implements OnInit {
  @Input() config: IALModalConfig;
  @Input() pageModal: PageModalController;

  currentEntry = {
    [ALConfigTypes.EXTENSION]: '',
  };
  LevelConfig = LevelConfig;
  MAX_CHARACTER_LIMIT = MAX_CHARACTER_LIMIT;
  alConfigTypes = [
    {
      type: ALConfigTypes.EXTENSION,
      whitelistType: 'whitelist', 
      blacklistType: 'blacklist', 
      description: 'caption_tech_readi_al_extensions', 
      addPlaceholder: 'lbl_al_config_add_extension'
    }
  ]

  constructor(
    private auth: AuthService,
    private loginGuard: LoginGuardService,
    public lang: LangService,
    private whitelabel: WhitelabelService,
    private routes: RoutesService,
  ) { }

  ngOnInit(): void {
    this.initConfiguration();
  }

  validateEntryUnique() {
    
  }

  public async initConfiguration() {
    // Reset all tags
    for(const level in LevelConfig) {
      LevelConfig[level].whitelist = [];
      LevelConfig[level].blacklist = [];
      LevelConfig[level].domain_whitelist = [];
    }

    // Fill in whitelist based on data passed from parent
    for (const level of this.config.levelData){
      const config = LevelConfig[level.level];
      config.whitelist = level.whitelist;
    }
  }

  public get currentConfig() {
    return LevelConfig[this.config.currentLevel]
  }

  public isEditable(config) {
    return config.order === this.currentConfig.order
  }

  public isShown(config) {
    return config.order <= this.currentConfig.order && config.shown !== false;
  }

  /** Renders only the levels that are shown */
  public get renderLevels() {
    return Object.values(LevelConfig)
      .filter(config => this.isShown(config)) 
      .sort((a, b) => a.order - b.order);
  }

  public removeTag(tag: string, type = 'whitelist', config = this.currentConfig, ) {
    config[type] = config[type].filter(t => t !== tag)
  }

  public async addTag(tag: string, type = 'whitelist', configType: ALConfigTypes = ALConfigTypes.EXTENSION, config = this.currentConfig) {
    tag = tag.trim();
    if (!tag) {
      return;
    }

    if (!config[type].includes(tag)) {
      if (this.getTotalListSize(tag) > MAX_CHARACTER_LIMIT){
        return this.loginGuard.quickPopup(this.getMaxCharLimitText());
      }
      console.log(this.getTotalListSize(tag))
      config[type].push(tag)
      this.currentEntry[configType] = '';
    } else {
      this.loginGuard.quickPopup('lbl_ldb_already_exists');
    }
  }


  /**
   * Calculates the total number of characters used by all whitelist,
   * blacklist, and domain whitelist entries across all assessment levels.
   *
   * Optionally includes the length of a new item to determine the resulting
   * total size if the item were added to the lists.
   *
   * @param newItem - Optional item to include in the size calculation.
   * @returns The total character count of all configured list entries,
   *          including the optional new item.
   */
  getTotalListSize(newItem?: string){

    const getDomainFromString = (inputDomain: string) => {
      const match = inputDomain.match(/(?:https?:\/\/)?(?:www\.)?([^\/\s]+)/);
      const domain = match ? match[1] : '';
      return domain;
    }

    let currSize = 0;
    for (const level of Object.values(LevelConfig)){
      const whiteListTags = level.whitelist.join('');
      currSize += whiteListTags.length;
      const blacklistTags = level.blacklist.join('');
      currSize += blacklistTags.length;
      const domainWhiteListTags = level.domain_whitelist.map(d => getDomainFromString(d)).join('');
      currSize += domainWhiteListTags.length;
    }
    if (newItem){
      currSize += newItem.length;
    }
    return currSize;
  }

  public async saveChanges() {
    if (!this.config?.boardInfo?.group_id){
      this.loginGuard.quickPopup('lbl_ldb_config_saving_err');
      return;
    }

    const res = await this.auth.apiPatch(
      this.routes.DIST_ADMIN_SUMMARY, 
      this.config.query.board_id, 
      {
        whitelist: this.currentConfig.whitelist,
        level: this.config.currentLevel
      }, 
      { query:{ school_district_group_id: this.config?.boardInfo?.group_id}}
    ).catch((err) => {
      if (err.message == 'MAX_LIST_CHARACTER_LIMIT_EXCEEDED'){
        this.loginGuard.quickPopup(this.getMaxCharLimitText());
      } else {
        this.loginGuard.quickPopup('lbl_ldb_config_saving_err');
      }
      this.closeModal();
      throw new Error('');
    });

    // sync with the cached data used in the parent tech readiness page. 
    this.config.boardInfo.asmt_lock.extensionAllowList_schoolBoard = this.currentConfig.whitelist;
    this.loginGuard.quickPopup('lbl_ldb_config_saving_suc');
    this.closeModal();
  }

  public closeModal() {
    this.pageModal.closeModal()
  }

  /**
   * Get maximum character limit reached error message
   * @returns {string}
   */
  getMaxCharLimitText():string {
    return this.lang.tra('lbl_ldb_char_limit_allow', undefined, { MAX_CHARACTER_LIMIT: MAX_CHARACTER_LIMIT })
  }

  /**
   * Get config level description text by role based on the whitelabel context
   * @param description can be a translation slug or a ALConfigLevel level
   * @returns {string} Translated description or role-based text
   */
  getLevelDescriptionText(description:ALConfigLevel):string {
    const roleLabels = {
      eqao: {
        [ALConfigLevel.BOARD]: 'lbl_ldb_config_lvl_brd_role_eqao'
      },
      abed: {
        [ALConfigLevel.BOARD]: 'lbl_ldb_config_lvl_brd_role_abed'
      },
    };

    // Check if the description is a valid ALConfigLevel value
    const isValidConfigLevel = Object.values(ALConfigLevel).includes(description as ALConfigLevel);
    if (!isValidConfigLevel) { // If not valid, assume it's a translation slug
      return this.lang.tra(description)
    }

    const currentLabelSet = this.whitelabel.isEQAO ? roleLabels.eqao : 
                            this.whitelabel.isABED ? roleLabels.abed : {};

    const roleKey = currentLabelSet[description as ALConfigLevel];
    const userRole = this.lang.tra(roleKey)

    // Return the translated text with the user role
    return this.lang.tra('lbl_ldb_configured_by', undefined, { USER_ROLE: userRole });
  }
}
